/** Authoritative units: 1 metre = 1,000 units; money = cuts; time = ticks. */
export const UNITS_PER_METRE = 1000;
export function integer(value: number): number {
  if (!Number.isSafeInteger(value)) throw new Error('Unsafe integer arithmetic');
  return value;
}
export const add = (a: number, b: number): number => integer(integer(a) + integer(b));
export function multiply(a: number, b: number): number {
  return integer(integer(a) * integer(b));
}
export function canonical(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') return JSON.stringify(integer(value));
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const record = value as Record<string, unknown>;
    return `{${Object.keys(record).sort().map(key => `${JSON.stringify(key)}:${canonical(record[key])}`).join(',')}}`;
  }
  throw new Error('Canonical state accepts only JSON data and safe integers');
}
/** FNV-1a over UTF-16 code units: replay diagnostics, never authentication. */
export function hash(value: unknown): string {
  let h = 0xcbf29ce484222325n;
  for (const unit of canonical(value).split('')) {
    h = BigInt.asUintN(64, (h ^ BigInt(unit.charCodeAt(0))) * 0x100000001b3n);
  }
  return h.toString(16).padStart(16, '0');
}
export const STREAMS = ['politics', 'people', 'quests', 'threats', 'hazards', 'loot', 'combat', 'ai', 'cosmetic'] as const;
export function randomStream(input: unknown, label: typeof STREAMS[number]): (max: number) => number {
  let state = Number.parseInt(hash({ input, label }).slice(-8), 16) || 1;
  return max => {
    if (!Number.isSafeInteger(max) || max < 1 || max > 0x100000000) throw new Error('Invalid random bound');
    // Rejection sampling avoids modulo bias and pins exact stream consumption.
    const limit = Math.floor(0x100000000 / max) * max;
    let result: number;
    do {
      state ^= state << 13; state ^= state >>> 17; state ^= state << 5;
      result = state >>> 0;
    } while (result >= limit);
    return result % max;
  };
}
export function freeze<T>(value: T): Readonly<T> {
  if (value !== null && typeof value === 'object') {
    for (const child of Object.values(value)) freeze(child);
    Object.freeze(value);
  }
  return value;
}
